import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  Paper,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  InputAdornment,
  Card,
  alpha,
  Pagination,
} from "@mui/material";
import {
  Cancel,
  Close,
  Group,
  PersonAdd,
  PersonRemove,
} from "@mui/icons-material";
import {
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Psychology as PsychologyIcon,
  Category as CategoryIcon,
  School as LevelIcon,
  Topic as TopicIcon,
  Assignment as TestTypeIcon,
  Quiz as QuestionTypeIcon,
  BarChart as BarChartIcon,
} from "@mui/icons-material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import "../../App.css";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { TableNodata } from "../../components/TableNodata";
import { TableLoader } from "../../components/TableLoader";
import VideocamIcon from "@mui/icons-material/Videocam";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";

const ViewTestDetails = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { assessment_Id } = location.state || {};
  const [testData, setTestData] = useState({});
  // Assign/unassign is only allowed while SCHEDULED or READY. Any other status
  // (OPEN/COMPLETED/CANCELLED, or anything new) is rejected by the backend — lock the
  // UI too. An absent status (still loading) is left unlocked; the backend guards it.
  const testStatusValue = (testData?.status || "").toUpperCase();
  const assignLocked =
    testStatusValue !== "" && !["SCHEDULED", "READY"].includes(testStatusValue);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchKey, setSearchKey] = useState(
    localStorage.getItem("searchvalue") || ""
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [assignedStudentsData, setAssignedStudentsData] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // #1976d2 primary color scheme
  const primaryColor = "#1976d2";
  const customColors = {
    primary: primaryColor,
    primaryLight: alpha(primaryColor, 0.1),
    primaryMedium: alpha(primaryColor, 0.3),
    primaryDark: "#1565c0",
    secondary: "#f5f5f5",
    accent: "#ffffff",
    cardBg: "#ffffff",
    hover: alpha(primaryColor, 0.04),
    gradient: `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(
      primaryColor,
      0.8
    )} 100%)`,
    lightGradient: `linear-gradient(135deg, ${alpha(
      primaryColor,
      0.1
    )} 0%, ${alpha(primaryColor, 0.05)} 100%)`,
  };

  const tableHeadings = [
    { label: "Student Name", key: "name", align: "left" },
    { label: "Email", key: "email", align: "left" },
    { label: "Contact Number", key: "phone", align: "left" },
    { label: "Gender", key: "gender", align: "left" },
    ...(localStorage.getItem("role") === "TRAINER" ||
    localStorage.getItem("role") === "INSTITUTE_ADMIN"
      ? [{ label: "Action", key: "action", align: "center" }]
      : []),
  ];

  // === Assign Student Handlers ===
  const handleAssignStudents = () => {
    setOpenDialog(true);
    setSelectedStudents([]);
  };

  const handleSearch = (e) => {
    localStorage.setItem("currentPage", 0);
    setPage(0);
    localStorage.setItem("searchvalue", e.target.value);
    setSearchKey(e.target.value);
  };

  // Custom Detail Item Component with #1976d2 theme
  const DetailItem = ({ icon, label, value, subValue, color = "primary" }) => (
    <Card
      sx={{
        background: customColors.cardBg,
        border: `1px solid ${alpha(customColors.primary, 0.2)}`,
        borderRadius: 2,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 25px ${alpha(customColors.primary, 0.15)}`,
          borderColor: alpha(customColors.primary, 0.4),
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 3 }}>
        <Avatar
          sx={{
            background: customColors.lightGradient,
            color: customColors.primary,
            width: 50,
            height: 50,
            border: `2px solid ${alpha(customColors.primary, 0.2)}`,
            boxShadow: `0 4px 15px ${alpha(customColors.primary, 0.2)}`,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="600"
            textTransform="uppercase"
            letterSpacing={1}
          >
            {label}
          </Typography>
          <Typography
            fontWeight="700"
            fontSize="1.1rem"
            color="text.primary"
            sx={{ mt: 0.5 }}
          >
            {value || "N/A"}
          </Typography>
          {subValue && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              {subValue}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );

  const handleEditButton = (boolean, id) => {
    navigate("/addCategory", {
      state: { edit: boolean, assessment_id: id },
    });
  };

  // === API Calls ===
  const fetchData = async () => {
    try {
      const payload = { assessment_id: assessment_Id };
      const res = await api.post("ViewAssessmentUsingID", payload);
      setTestData(res.data?.data || {});
    } catch (error) {
      console.log("err :", error);
    }
  };

  const studentFetchData = async () => {
    try {
      const res = await api.post("ListActiveStudents", {
        assessment_id: assessment_Id,
      });
      setStudents(res.data?.data || []);
    } catch (error) {
      console.log("Error fetching students:", error);
      setStudents([]);
    }
  };

  const handleAssign = async () => {
    const ids = selectedStudents.map((s) => s.user_id);
    setLoading(true);
    try {
      const payload = { user_ids: ids, assessment_id: assessment_Id };
      const res = await api.post("StudentAssessmentList", payload);
      if (res?.data?.status === "success") {
        showSnackbar("success", res?.data?.message);
        setSelectedStudents([]);
        setOpenDialog(false);
        studentData();
        studentFetchData();
      } else {
        showSnackbar(
          "error",
          res?.data?.message || "Failed to assign",
          "",
          10000
        );
      }
    } catch (error) {
      console.log("Assign student error:", error);
      showSnackbar("error", error?.response?.data?.message || error?.message || "Failed to assign student. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const studentData = async () => {
    setLoading(true);
    try {
      const payload = {
        page,
        size: rowsPerPage,
        search_key: searchKey,
        assessment_id: assessment_Id,
      };
      const res = await api.post("ListStudentsAssignedToAssessment", payload);
      if (res?.data?.status === "success") {
        setAssignedStudentsData(res?.data?.data || []);
        setCount(res?.data?.count || 0);
      } else {
        showSnackbar("error", res?.data?.message || "No data found");
      }
    } catch (error) {
      console.log("Error fetching assigned students:", error);
      showSnackbar("error", error?.response?.data?.message || error?.message || "Failed to fetch assigned students. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const unAssignStudent = async (id) => {
    setLoading(true);
    try {
      const payload = { user_id: [id], assessment_id: assessment_Id };
      const res = await api.post("UnassignStudentFromAssessment", payload);
      if (res?.data?.status === "success") {
        showSnackbar("success", res?.data?.message);
        studentData();
        studentFetchData();
      } else {
        showSnackbar("error", res?.data?.message || "Failed to unassign");
      }
    } catch (error) {
      console.log("Unassign student error:", error);
      showSnackbar("error", error?.response?.data?.message || error?.message || "Failed to unassign student. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // === useEffects ===
  useEffect(() => {
    fetchData();
    if (localStorage?.getItem("role") !== "SUPER_ADMIN") {
      studentFetchData();
    }
  }, []);

  useEffect(() => {
    studentData();
  }, [page, rowsPerPage, searchKey]);

  // === UI ===
  return (
    <Box
      sx={{
        p: 1,
        maxWidth: "1400px",
        margin: "0 auto",
        minHeight: "100vh",
        background: "transparent",
      }}
    >
      {/* Back button - #1976d2 Style */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          color: customColors.primary,
          gap: 1,
          cursor: "pointer",
          width: "fit-content",
          mb: 3,
          p: 1,
          borderRadius: 1,
          transition: "all 0.3s ease",
          "&:hover": {
            background: customColors.primaryLight,
            transform: "translateX(-4px)",
          },
        }}
        onClick={() => navigate(-1)}
      >
        <ArrowBackIcon sx={{ fontSize: "20px" }} />
        <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>Back</Typography>
      </Box>

      {/* Hero Header Section - #1976d2 Style */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 3,
          background: customColors.cardBg,
          border: `1px solid ${alpha(customColors.primary, 0.2)}`,
          boxShadow: `0 10px 20px ${alpha(customColors.primary, 0.1)}`,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={3}
        >
          <Box sx={{ flex: 1 }}>
            <Box display="flex" alignItems="start" gap={2} mb={2}>
              <Box
                sx={{
                  background: customColors.gradient,
                  minWidth: "50px",
                  minHeight: "50px",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 3px 10px ${alpha(customColors.primary, 0.3)}`,
                }}
              >
                <CategoryIcon
                  sx={{ color: customColors.accent, fontSize: "30px" }}
                />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  fontWeight="800"
                  sx={{
                    color: customColors.primary,
                    textShadow: `0 2px 10px ${alpha(
                      customColors.primary,
                      0.2
                    )}`,
                  }}
                >
                  {testData.category || "Assessment"}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" color="text.secondary">
              {testData.created_at
                ? `Created on ${new Date(
                    testData.created_at
                  ).toLocaleDateString()}`
                : ""}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {testData.created_by
                ? `Created By ${testData.created_by}`
                : "Professional Assessment"}
            </Typography>
          </Box>
          {localStorage.getItem("role") !== "SUPER_ADMIN" &&
            localStorage.getItem("role") !== "STUDENT" && (
              <Box display="flex" gap={2}>
                <Tooltip
                  title={
                    testData?.deprecated === "YES"
                      ? "⚠ Previewing is disabled"
                      : null
                  }
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "primary.main",
                        color: "white",
                        fontSize: 13,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        width: "fit-content",
                        maxWidth: "none",
                        padding: "6px 10px",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "primary.main",
                      },
                    },
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<PreviewOutlinedIcon />}
                    sx={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      borderColor: customColors.primary,
                      color: customColors.primary,
                      fontWeight: 600,
                      px: 3,
                      cursor:
                        testData?.deprecated === "YES"
                          ? "not-allowed"
                          : "pointer",
                      "&:hover": {
                        borderColor: customColors.primaryDark,
                        background: customColors.primaryLight,
                        transform: "translateY(-2px)",
                        boxShadow: `0 6px 20px ${alpha(
                          customColors.primary,
                          0.2
                        )}`,
                      },
                      transition: "all 0.3s ease",
                    }}
                    onClick={() =>
                      testData?.deprecated === "YES"
                        ? null
                        : navigate("/previewTable", {
                            state: {
                              assessment_Id: testData?.assessment_id,
                              initial: true,
                            },
                          })
                    }
                  >
                    Question Paper Preview{" "}
                  </Button>
                </Tooltip>
                <Tooltip
                  title={
                    testData?.deprecated === "YES"
                      ? "⚠ Assigning is disabled"
                      : assignLocked
                      ? "Students can only be assigned while the assessment is Scheduled or Ready."
                      : testData?.no_of_previews < 3
                      ? "Please preview the question paper before assigning a student. Minimum no of previews of 3 required."
                      : ""
                  }
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "primary.main",
                        color: "white",
                        fontSize: 13,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        width: "fit-content",
                        maxWidth: "none",
                        padding: "6px 10px",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "primary.main",
                      },
                    },
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    sx={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      borderColor: customColors.primary,
                      color: customColors.primary,
                      fontWeight: 600,
                      cursor:
                        testData?.deprecated === "YES"
                          ? "not-allowed"
                          : assignLocked
                          ? "not-allowed"
                          : testData?.no_of_previews < 3
                          ? "not-allowed"
                          : "pointer",
                      px: 3,
                      "&:hover": {
                        borderColor: customColors.primaryDark,
                        background: customColors.primaryLight,
                        transform: "translateY(-2px)",
                        boxShadow: `0 6px 20px ${alpha(
                          customColors.primary,
                          0.2
                        )}`,
                      },
                      transition: "all 0.3s ease",
                    }}
                    onClick={
                      testData?.deprecated === "YES"
                        ? null
                        : assignLocked
                        ? null
                        : testData?.no_of_previews < 3
                        ? null
                        : handleAssignStudents
                    }
                  >
                    Assign Students
                  </Button>
                </Tooltip>
                <Tooltip
                  title={
                    testData?.deprecated === "YES"
                      ? "⚠ Editing is disabled"
                      : null
                  }
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "primary.main",
                        color: "white",
                        fontSize: 13,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        width: "fit-content",
                        maxWidth: "none",
                        padding: "6px 10px",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "primary.main",
                      },
                    },
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    sx={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      background: customColors.gradient,
                      fontWeight: 600,
                      px: 3,
                      cursor:
                        testData?.deprecated === "YES"
                          ? "not-allowed"
                          : "pointer",
                      boxShadow: `0 4px 15px ${alpha(
                        customColors.primary,
                        0.3
                      )}`,
                      "&:hover": {
                        background: customColors.gradient,
                        transform: "translateY(-2px)",
                        boxShadow: `0 8px 25px ${alpha(
                          customColors.primary,
                          0.4
                        )}`,
                      },
                      transition: "all 0.3s ease",
                    }}
                    onClick={() =>
                      testData?.deprecated === "YES"
                        ? null
                        : handleEditButton(true, testData.assessment_id)
                    }
                  >
                    Edit Question Paper
                  </Button>
                </Tooltip>
              </Box>
            )}
        </Box>
      </Paper>

      {/* Assign Students Dialog - #1976d2 Style */}
      <Dialog
        open={openDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            background: customColors.cardBg,
            border: `1px solid ${alpha(customColors.primary, 0.2)}`,
            boxShadow: `0 20px 60px ${alpha(customColors.primary, 0.15)}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: customColors.lightGradient,
            borderBottom: `1px solid ${alpha(customColors.primary, 0.2)}`,
            py: 2,
            px: 3,
            mb: 2,
          }}
        >
          <Box display="flex" alignItems="center">
            <PersonAdd sx={{ color: customColors.primary, mr: 1 }} />
            <Typography variant="h6" fontWeight={600} color="text.primary">
              Assign Students
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOpenDialog(false)}
            size="small"
            sx={{ color: customColors.primary }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 3, background: customColors.cardBg }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={students || []}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) =>
              option.user_id === value.user_id
            }
            value={selectedStudents}
            onChange={(event, newValue) => setSelectedStudents(newValue)}
            renderOption={(props, option, { selected }) => (
              <li
                {...props}
                style={{
                  padding: "12px 16px",
                  background: selected
                    ? customColors.primaryLight
                    : customColors.cardBg,
                  borderBottom: `1px solid ${alpha(customColors.primary, 0.1)}`,
                }}
              >
                <Checkbox
                  checked={selected}
                  sx={{
                    color: customColors.primary,
                    "&.Mui-checked": {
                      color: customColors.primary,
                    },
                  }}
                />
                <Box>
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    color="text.primary"
                  >
                    {option.name}
                  </Typography>
                  {option.email && (
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search students..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    background: alpha(customColors.primary, 0.03),
                    border: `1px solid ${alpha(customColors.primary, 0.2)}`,
                    color: "text.primary",
                    "&:hover": {
                      borderColor: customColors.primary,
                    },
                    "&.Mui-focused": {
                      borderColor: customColors.primary,
                    },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <Group
                        sx={{ color: customColors.primary, mr: 1, ml: 0.5 }}
                      />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${alpha(customColors.primary, 0.2)}`,
            backgroundColor: customColors.lightGradient,
          }}
        >
          <Button
            onClick={() => setOpenDialog(false)}
            startIcon={<Cancel />}
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            startIcon={<PersonAdd />}
            disabled={selectedStudents.length === 0 || loading}
            sx={{
              background: customColors.gradient,
              fontWeight: 600,
              "&:hover": {
                background: customColors.gradient,
                transform: "translateY(-1px)",
                boxShadow: `0 6px 20px ${alpha(customColors.primary, 0.3)}`,
              },
              transition: "all 0.3s ease",
            }}
          >
            Assign{" "}
            {selectedStudents.length > 0 ? `(${selectedStudents.length})` : ""}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assessment Details Grid - #1976d2 Style */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              background: customColors.cardBg,
              border: `1px solid ${alpha(customColors.primary, 0.2)}`,
              boxShadow: `0 8px 30px ${alpha(customColors.primary, 0.1)}`,
            }}
          >
            <Typography
              variant="h4"
              fontWeight="700"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                color: customColors.primary,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  background: customColors.gradient,
                  width: 50,
                  height: 50,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PsychologyIcon
                  sx={{ color: customColors.accent, fontSize: "24px" }}
                />
              </Box>
              Question Paper Details
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <DetailItem
                  icon={<CategoryIcon />}
                  label="Subject"
                  value={testData.category}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DetailItem
                  icon={<LevelIcon />}
                  label="Difficulty Level"
                  value={testData.level}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DetailItem
                  icon={<TopicIcon />}
                  label="Topics Covered"
                  value={
                    Array.isArray(testData.topics)
                      ? testData.topics.join(", ")
                      : "-"
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DetailItem
                  icon={<HelpOutlineIcon />}
                  label="No. of Questions"
                  value={testData.no_of_questions + " Questions"}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DetailItem
                  icon={<TestTypeIcon />}
                  label="Assessment Type"
                  value={testData.test_type}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DetailItem
                  icon={<QuestionTypeIcon />}
                  label="Question Type"
                  value={testData.question_type}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DetailItem
                  icon={<BarChartIcon />}
                  label="Time Duration"
                  value={
                    testData?.time_limit
                      ? `${testData.time_limit.replace(/\D/g, "")} min`
                      : "Not Specified"
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DetailItem
                  icon={<VideocamIcon />}
                  label="Camera Monitoring"
                  value={
                    testData.camera_assigned_status === "YES"
                      ? "Enabled"
                      : "Disabled"
                  }
                />
              </Grid>
              {/* <Grid item xs={12} sm={6} md={4}>
                <DetailItem
                  icon={<TestTypeIcon />}
                  label="No. of Previews"
                  value={testData.no_of_previews}
                />
              </Grid> */}
            </Grid>
          </Card>
        </Grid>
      </Grid>

      {/* Assigned Students Table - #1976d2 Style */}
      {localStorage.getItem("role") !== "STUDENT" && (
        <Box mt={4}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              background: customColors.cardBg,
              border: `1px solid ${alpha(customColors.primary, 0.2)}`,
              boxShadow: `0 8px 30px ${alpha(customColors.primary, 0.1)}`,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                color={customColors.primary}
              >
                Assigned Students
              </Typography>
              <TextField
                value={searchKey}
                onChange={handleSearch}
                size="small"
                variant="outlined"
                placeholder="Search students..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    background: alpha(customColors.primary, 0.03),
                    border: `1px solid ${alpha(customColors.primary, 0.2)}`,
                    color: "text.primary",
                    "&:hover": {
                      borderColor: customColors.primary,
                    },
                    "&.Mui-focused": {
                      borderColor: customColors.primary,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ color: alpha(customColors.primary, 0.6) }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TableContainer
              sx={{
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {tableHeadings.map((heading, index) => (
                      <TableCell
                        key={index}
                        align={heading.align}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          color: "text.primary",
                          borderBottom: `2px solid ${alpha(
                            customColors.primary,
                            0.3
                          )}`,
                          backgroundColor: customColors.primaryLight,
                          py: 2,
                        }}
                      >
                        {heading.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                {loading ? (
                  <TableBody>
                    <TableRow>
                      <TableCell
                        colSpan={tableHeadings.length}
                        align="center"
                        sx={{ py: 4 }}
                      >
                        <TableLoader message="Fetching details..." />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {(assignedStudentsData || []).length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={tableHeadings.length}
                          align="center"
                          sx={{ py: 4 }}
                        >
                          <TableNodata />
                        </TableCell>
                      </TableRow>
                    ) : (
                      assignedStudentsData.map((student) => (
                        <TableRow
                          key={student.id}
                          hover
                          sx={{
                            background: customColors.cardBg,
                            "&:hover": {
                              background: customColors.hover,
                              transform: "scale(1.01)",
                              transition: "all 0.2s ease",
                            },
                            "&:last-child td": { borderBottom: 0 },
                          }}
                        >
                          <TableCell
                            sx={{
                              borderBottom: `1px solid ${alpha(
                                customColors.primary,
                                0.1
                              )}`,
                            }}
                          >
                            <Typography fontWeight={600} color="text.primary">
                              {student.first_name + " " + student.last_name}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: `1px solid ${alpha(
                                customColors.primary,
                                0.1
                              )}`,
                              color: "text.secondary",
                            }}
                          >
                            {student.email}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: `1px solid ${alpha(
                                customColors.primary,
                                0.1
                              )}`,
                              color: "text.secondary",
                            }}
                          >
                            {student.phone}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: `1px solid ${alpha(
                                customColors.primary,
                                0.1
                              )}`,
                              color: "text.secondary",
                            }}
                          >
                            {student.gender}
                          </TableCell>
                          {(localStorage.getItem("role") === "TRAINER" ||
                            localStorage.getItem("role") ===
                              "INSTITUTE_ADMIN") && (
                            <TableCell
                              align="center"
                              sx={{
                                borderBottom: `1px solid ${alpha(
                                  customColors.primary,
                                  0.1
                                )}`,
                              }}
                            >
                              <Tooltip
                                title={
                                  assignLocked
                                    ? "Students can only be assigned while the assessment is Scheduled or Ready."
                                    : "Unassign Student"
                                }
                                placement="top"
                                arrow
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: "primary.main",
                                      color: "white",
                                      fontSize: 13,
                                      fontWeight: 500,
                                      whiteSpace: "nowrap",
                                      width: "fit-content",
                                      maxWidth: "none",
                                      padding: "6px 10px",
                                    },
                                  },
                                  arrow: {
                                    sx: {
                                      color: "primary.main",
                                    },
                                  },
                                }}
                              >
                                <IconButton
                                  sx={{
                                    color: customColors.primary,
                                    "&:hover": {
                                      background: customColors.primaryLight,
                                      transform: "scale(1.1)",
                                    },
                                    transition: "all 0.2s ease",
                                  }}
                                  size="small"
                                  disabled={loading || assignLocked}
                                  onClick={() =>
                                    unAssignStudent(student.user_id)
                                  }
                                >
                                  <PersonRemove fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
            {((page === 0 && assignedStudentsData?.length > rowsPerPage - 1) ||
              page !== 0) && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 2,
                }}
              >
                {page !== 0 ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={
                        <ArrowBackIcon
                          sx={{
                            color: page === 0 ? "#a2a2a2" : "#414651",
                          }}
                        />
                      }
                      disabled={page === 0}
                      onClick={() => setPage(page - 1)}
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        color: page === 0 ? "#a2a2a2" : "#414651",
                        borderColor: "#d0d0d0",
                      }}
                    >
                      Previous
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ width: "110px" }} />
                )}

                <Pagination
                  page={page + 1}
                  onChange={(e, value) => setPage(value - 1)}
                  count={Math.ceil(count / rowsPerPage)}
                  siblingCount={3}
                  boundaryCount={3}
                  shape="rounded"
                  hidePrevButton
                  hideNextButton
                />
                {page + 1 !== Math.ceil(count / rowsPerPage) ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="outlined"
                      endIcon={
                        <ArrowForwardIcon
                          sx={{
                            color: page + 1 === count ? "#a2a2a2" : "#414651",
                          }}
                        />
                      }
                      disabled={page + 1 === count}
                      onClick={() => setPage(page + 1)}
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        color: page + 1 === count ? "#a2a2a2" : "#414651",
                        borderColor: "#d0d0d0",
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ width: "110px" }} />
                )}
              </Box>
            )}
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default ViewTestDetails;
